export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  maxStoredLogs: number;
  storageKey: string;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4,
};

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  debug: '#6B7280',
  info: '#3B82F6',
  warn: '#F59E0B',
  error: '#EF4444',
  fatal: '#DC2626',
};

class Logger {
  private static instance: Logger;
  private config: LoggerConfig;
  private sessionId: string;
  private userId: string | null = null;
  private logs: LogEntry[] = [];

  private constructor() {
    const isDev = import.meta.env.DEV;
    this.config = {
      minLevel: isDev ? 'debug' : 'warn',
      enableConsole: isDev,
      maxStoredLogs: 200,
      storageKey: 'app_logs',
    };
    this.sessionId = this.generateSessionId();
    this.loadStoredLogs();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private loadStoredLogs(): void {
    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch {
      this.logs = [];
    }
  }

  private saveLogs(): void {
    try {
      if (this.logs.length > this.config.maxStoredLogs) {
        this.logs = this.logs.slice(-this.config.maxStoredLogs);
      }
      localStorage.setItem(this.config.storageKey, JSON.stringify(this.logs));
    } catch {
      // Storage full or unavailable
    }
  }

  setUserId(userId: string | null): void {
    const nextKey = userId ? `app_logs:${userId}` : 'app_logs';
    if (this.userId === userId && this.config.storageKey === nextKey) return;

    this.userId = userId;
    this.config.storageKey = nextKey;

    if (userId) {
      try {
        const legacy = localStorage.getItem('app_logs');
        if (legacy && !localStorage.getItem(nextKey)) {
          localStorage.setItem(nextKey, legacy);
        }
        localStorage.removeItem('app_logs');
      } catch {
        // ignore
      }
    }

    this.logs = [];
    this.loadStoredLogs();
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.minLevel];
  }

  private formatMessage(entry: LogEntry): string {
    const parts = [entry.message];
    if (entry.context) {
      parts.unshift(`[${entry.context}]`);
    }
    return parts.join(' ');
  }

  private log(level: LogLevel, message: string, context?: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      userId: this.userId || undefined,
      sessionId: this.sessionId,
    };

    this.logs.push(entry);
    this.saveLogs();

    if (this.config.enableConsole) {
      const formattedMsg = this.formatMessage(entry);
      const color = LOG_LEVEL_COLORS[level];
      const prefix = `%c[${level.toUpperCase()}]`;
      const style = `color: ${color}; font-weight: bold;`;

      switch (level) {
        case 'debug':
          console.debug(prefix, style, formattedMsg, data || '');
          break;
        case 'info':
          console.info(prefix, style, formattedMsg, data || '');
          break;
        case 'warn':
          console.warn(prefix, style, formattedMsg, data || '');
          break;
        case 'error':
        case 'fatal':
          console.error(prefix, style, formattedMsg, data || '');
          break;
      }
    }
  }

  debug(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log('debug', message, context, data);
  }

  info(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log('info', message, context, data);
  }

  warn(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log('warn', message, context, data);
  }

  error(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log('error', message, context, data);
  }

  fatal(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log('fatal', message, context, data);
  }

  getLogs(level?: LogLevel, limit?: number): LogEntry[] {
    let filtered = this.logs;
    if (level) {
      const minPriority = LOG_LEVEL_PRIORITY[level];
      filtered = filtered.filter(log => LOG_LEVEL_PRIORITY[log.level] >= minPriority);
    }
    if (limit) {
      filtered = filtered.slice(-limit);
    }
    return filtered;
  }

  getRecentErrors(limit = 10): LogEntry[] {
    return this.logs
      .filter(log => log.level === 'error' || log.level === 'fatal')
      .slice(-limit);
  }

  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem(this.config.storageKey);
  }

  exportLogs(): string {
    return JSON.stringify({
      exportedAt: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      logs: this.logs,
    }, null, 2);
  }

  downloadLogs(): void {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const logger = Logger.getInstance();

export function createContextLogger(context: string) {
  return {
    debug: (message: string, data?: Record<string, unknown>) => logger.debug(message, context, data),
    info: (message: string, data?: Record<string, unknown>) => logger.info(message, context, data),
    warn: (message: string, data?: Record<string, unknown>) => logger.warn(message, context, data),
    error: (message: string, data?: Record<string, unknown>) => logger.error(message, context, data),
    fatal: (message: string, data?: Record<string, unknown>) => logger.fatal(message, context, data),
  };
}
